import { useEffect, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { reporterHome } from "@/lib/home";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { TextAssist } from "@/components/TextAssist";
import { AdminPhoneVerify } from "@/components/AdminPhoneVerify";
import { ReachabilityBadge } from "@/components/ReachabilityBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Siren, ShieldAlert, Users, MessageSquareText, CheckCircle2, AlertTriangle,
  Loader2, Send, Save, Phone, Clock, PhoneOff, Sparkles, ListChecks,
  Plus, Trash2, PhoneCall, Timer, Mail, Settings2, History,
} from "lucide-react";
import { ReporterBlock, ResolveDialog, fmtPhone } from "@/components/EmergencyBits";
import { LadderFlow, ContactedPanel, PhraseTester } from "@/components/EmergencyLadderBits";
import { WordsEditor } from "@/components/EmergencyWords";
import { EmergencyTimelineDialog, IncidentPdfButton } from "@/components/EmergencyRecord";
import { EmergencyReadiness } from "@/components/EmergencyReadiness";
import { CollapsibleCard } from "@/components/CollapsibleCard";
import { ModuleSettingsDialog } from "@/components/ModuleSettingsDialog";
import { StickySaveBar } from "@/components/StickySaveBar";
import { V } from "@/lib/vocab";

const fmtDate = (d) => (d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

// Every text a RESIDENT can ever receive from this module is in the first group — fixed wording,
// no AI. The second group goes to your own responders.
const msgFieldsFactory = () => [
  { group: "resident", key: "resident_confirm", label: "Possible emergency — confirm", hint: "Sent when a possible emergency is detected. If we don't know the number, a request for name + unit is added automatically." },
  { group: "resident", key: "resident_alerted", label: "Team alerted", hint: "Sent once your response ladder has been notified." },
  { group: "resident", key: "resident_photo_ask", label: "Photo ask (optional)", hint: "Appended to “Team alerted” when the switch is on under Module settings. Photos texted back go on the record and out to the responders." },
  { group: "resident", key: "resident_photo_received", label: "Photo received", hint: "The one-line receipt when a reporter texts a photo during an open emergency." },
  { group: "resident", key: "resident_ack", label: "Someone took it", hint: `Sent when a responder acknowledges. {responder} and {responder_title} (e.g. ${V.one("org")} Manager, ${V.Grp()} President) available.` },
  { group: "resident", key: "all_clear", label: "All-clear", hint: "Only sent after a human explicitly says so — by text (YES) or the tick-box on Mark resolved." },
  { group: "responder", key: "alert_sms", label: "Responder alert text", hint: "{issue} {reporter} {unit} {reporter_phone} {roster_flag} {link} available." },
  { group: "responder", key: "stand_down", label: "Stand-down to other responders", hint: "{responder} and {responder_title} available." },
  { group: "responder", key: "followup_ask", label: "Resolve follow-up to the owner", hint: "Asked once if the owner has said nothing since taking it." },
  { group: "responder", key: "last_resort_sms", label: "Last-resort blast", hint: "When nobody acknowledged in time. Same placeholders as the alert." },
];

const NumField = ({ label, value, onChange, testid, suffix = "min" }) => (
  <label className="flex items-center justify-between gap-3 rounded-xl border border-sand-200 bg-white px-4 py-2.5">
    <span className="text-sm font-semibold text-ink">{label}</span>
    <span className="flex items-center gap-1.5">
      <input type="number" min={1} data-testid={testid} value={value}
        onChange={(e) => onChange(Math.max(1, parseInt(e.target.value || "1", 10)))}
        className="w-16 rounded-lg border border-sand-300 px-2 py-1 text-right text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
      <span className="text-xs text-ink/50">{suffix}</span>
    </span>
  </label>
);


const Toggle = ({ checked, onChange, label, desc, testid }) => (
  <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
    className="flex w-full items-start gap-3 rounded-xl border border-sand-200 bg-white px-4 py-3 text-left transition-colors hover:border-ink/30">
    <span className={`mt-0.5 flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-redline-500" : "bg-sand-300"}`}>
      <span className={`h-4 w-4 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
    </span>
    <span className="min-w-0">
      <span className="block text-sm font-semibold text-ink">{label}</span>
      {desc && <span className="mt-0.5 block text-xs leading-snug text-ink/50">{desc}</span>}
    </span>
  </button>
);

const StatusPill = ({ status }) => {
  const map = {
    alerting: ["bg-red-50 text-red-700 border-red-200", "Alerting"],
    acknowledged: ["bg-amber-50 text-amber-800 border-amber-200", "Owned"],
    resolved: ["bg-emerald-50 text-emerald-700 border-emerald-200", "Resolved"],
    expired: ["bg-sand-200 text-ink/60 border-sand-300", "Expired"],
  };
  const [cls, txt] = map[status] || map.expired;
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-bold ${cls}`}>{txt}</span>;
};

export default function Emergency() {
  const MSG_FIELDS = msgFieldsFactory();
  const { tenantId, role, canConfigure, tenant, refreshTenants, me } = useDash();
  const canEdit = role !== "board_member" || !!canConfigure;
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);   // shipped starting words — "Reset to defaults"
  const [params, setParams] = useSearchParams();
  const wantDetection = params.get("detection") === "1";   // "Edit the emergency words" deep link from Settings
  const [responders, setResponders] = useState([]);
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(null);
  const [respQuery, setRespQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [saveTick, setSaveTick] = useState(0);
  const [savedSnap, setSavedSnap] = useState("");
  // Timing rides along with the ladder (owner: "it's their response plan") — managers edit the
  // step waits, the re-alert delay and the master timeout inline, saved with the same Save button.
  const SAVE_FIELDS = ["keywords", "critical_keywords", "require_confirmation", "critical_skip_confirm", "ai_backstop", "tiers", "fallback_emails", "messages",
                       "call_delay_minutes", "no_ack_timeout_minutes", "unacked_repeat_minutes", "unacked_repeat_max", "alert_order"];
  const snapOf = (c) => JSON.stringify(SAVE_FIELDS.map((k) => c?.[k] ?? null));
  const dirty = !!cfg && !!savedSnap && snapOf(cfg) !== savedSnap;

  const load = useCallback(async () => {
    if (!tenantId) return;   // leaving the community (back arrow) — nothing to load, nothing to complain about
    setLoading(true);
    try {
      const [c, r, a, e] = await Promise.all([
        api.get(`/tenants/${tenantId}/emergency/config`),
        api.get(`/tenants/${tenantId}/emergency/eligible-responders`),
        api.get(`/tenants/${tenantId}/emergency/active`),
        api.get(`/tenants/${tenantId}/emergency/events`),
      ]);
      setCfg(c.data.config);
      setDefaults(c.data.defaults || null);
      setSavedSnap(snapOf(c.data.config));
      setResponders(r.data.responders || []);
      setActive(a.data.active || null);
      setEvents(e.data.events || []);
    } catch (err) {
      if (err?.response?.status === 403) setDenied(true);
      else if (err?.response?.status === 404 || err?.response?.status === 401) setDenied(true);
      else toast.error("Couldn't load emergency settings");
    } finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);
  // The "who's been contacted" list changes by the minute while an emergency is open — keep it live.
  useEffect(() => {
    if (!tenantId) return;
    const t = setInterval(() => api.get(`/tenants/${tenantId}/emergency/active`).then((r) => setActive(r.data.active || null)).catch(() => {}), 15000);
    return () => clearInterval(t);
  }, [tenantId]);
  useEffect(() => {
    if (!wantDetection || !cfg) return;
    const t = setTimeout(() => document.querySelector("[data-testid='emergency-detection-card']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    return () => clearTimeout(t);
  }, [wantDetection, cfg]);   // eslint-disable-line react-hooks/exhaustive-deps

  const tiers = cfg?.tiers || [];
  const setTiers = (t) => setCfg((c) => ({ ...c, tiers: t }));
  const updateTier = (i, patch) => setTiers(tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const toggleInTier = (i, uid) => {
    const ids = tiers[i]?.user_ids || [];
    updateTier(i, { user_ids: ids.includes(uid) ? ids.filter((x) => x !== uid) : [...ids, uid] });
  };
  const addTier = () => setTiers([...tiers, { name: `Tier ${tiers.length + 1}`, user_ids: [], wait_minutes: 7, channels: { sms: true, call: true, email: false } }]);
  const [pickerTier, setPickerTier] = useState(null);
  const removeTier = (i) => setTiers(tiers.filter((_, idx) => idx !== i));
  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const setMsg = (k, v) => setCfg((c) => ({ ...c, messages: { ...(c.messages || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        keywords: cfg.keywords,
        critical_keywords: cfg.critical_keywords,
        require_confirmation: cfg.require_confirmation,
        critical_skip_confirm: cfg.critical_skip_confirm,
        ai_backstop: cfg.ai_backstop,
        tiers: cfg.tiers,
        fallback_emails: cfg.fallback_emails,
        messages: cfg.messages,
        call_delay_minutes: cfg.call_delay_minutes,
        no_ack_timeout_minutes: cfg.no_ack_timeout_minutes,
        unacked_repeat_minutes: cfg.unacked_repeat_minutes,
        unacked_repeat_max: cfg.unacked_repeat_max,
        alert_order: cfg.alert_order,
      };
      const r = await api.put(`/tenants/${tenantId}/emergency/config`, body);
      setCfg(r.data.config);
      setSavedSnap(snapOf(r.data.config));
      setSaveTick((n) => n + 1);
      toast.success("Emergency settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  const discard = async () => {
    try { const c = await api.get(`/tenants/${tenantId}/emergency/config`); setCfg(c.data.config); setSavedSnap(snapOf(c.data.config)); }
    catch { toast.error("Couldn't reload settings"); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/emergency/test`);
      if (r.data.responders === 0) toast.error("No responders with a verified phone on the list yet");
      else { toast.success(`Test alert sent to ${r.data.sent} of ${r.data.responders} responder(s)`); setTestSent(true); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send test"); }
    finally { setTesting(false); }
  };

  const remindVerify = async (ids) => {
    setReminding(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/emergency/remind-verify`, { user_ids: ids });
      toast.success(`Reminder sent to ${r.data.sent} responder(s)`);
    } catch (e) { toast.error("Couldn't send reminder"); }
    finally { setReminding(false); }
  };

  const finishSetup = async () => {
    try {
      const r = await api.put(`/tenants/${tenantId}/emergency/config`, { setup_done: true });
      setCfg(r.data.config);
      toast.success("Emergency Alerts is set up");
    } catch (e) { toast.error("Couldn't save"); }
  };

  const [resolveOpen, setResolveOpen] = useState(false);
  const [cfgOpen, setCfgOpen] = useState(false);
  const recordId = params.get("event");
  const tabParam = params.get("tab");
  const setTab = (t) => { const p = new URLSearchParams(params); if (t === "incidents") p.delete("tab"); else p.set("tab", t); setParams(p, { replace: true }); };
  const openRecord = (id) => { const p = new URLSearchParams(params); p.set("event", id); setParams(p); };
  const closeRecord = () => { const p = new URLSearchParams(params); p.delete("event"); setParams(p, { replace: true }); };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-redline-500" /></div>;
  if (denied) return (
    <div data-testid="emergency-denied" className="mx-auto max-w-lg rounded-2xl border border-sand-200 bg-white p-8 text-center">
      <Siren className="mx-auto h-8 w-8 text-ink/30" />
      <h2 className="mt-3 text-lg font-bold text-ink">Emergency Escalation isn't enabled</h2>
      <p className="mt-1 text-sm text-ink/60">This is a premium add-on. Ask your account admin to enable it in Billing &amp; Plans.</p>
    </div>
  );
  if (!cfg) return null;

  const allSelected = new Set(tiers.flatMap((t) => t.user_ids || []));
  const callableCount = responders.filter((r) => allSelected.has(r.user_id) && r.phone_verified && r.call_ok !== false).length;
  const onLadder = responders.filter((r) => allSelected.has(r.user_id));
  const unverifiedOnLadder = onLadder.filter((r) => !r.phone_verified);
  const showSetup = canEdit && !cfg.setup_done;
  // Incidents first — that's what people come here for. Setup leads only until the module is set up and nothing has happened yet.
  const tab = tabParam === "setup" || tabParam === "incidents" ? tabParam : (wantDetection || (showSetup && events.length === 0 && !active) ? "setup" : "incidents");
  const openIncidents = events.filter((e) => e.status === "alerting" || e.status === "acknowledged").length;
  const stepsDone = [onLadder.length >= 1, onLadder.length >= 1 && unverifiedOnLadder.length === 0, testSent];

  return (
    <div data-testid="emergency-page" className="mx-auto max-w-4xl space-y-6 pb-16">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50">
            <Siren className="h-5 w-5 text-red-600" strokeWidth={2} />
          </div>
          <div>
            <PageTitle title="Emergency Escalation" about="Detects property emergencies and instantly alerts your response team." />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit ? (
            <>
              <button data-testid="emergency-module-settings" onClick={() => setCfgOpen(true)} title="Module settings — switches and set-once numbers"
                className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-white px-4 py-2 text-sm font-semibold text-ink hover:border-ink">
                <Settings2 className="h-4 w-4" /> Module settings
              </button>
              <button data-testid="emergency-test-btn" onClick={sendTest} disabled={testing}
                className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-white px-4 py-2 text-sm font-semibold text-ink hover:border-ink disabled:opacity-50">
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test alert
              </button>
              {tab === "setup" && (
                <button data-testid="emergency-save-btn" onClick={save} disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2 text-sm font-semibold text-white hover:bg-ink-700 disabled:opacity-50">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </button>
              )}
            </>
          ) : (
            <span data-testid="emergency-readonly-pill" className="inline-flex items-center gap-1.5 rounded-full bg-sand-200 px-3 py-1.5 text-xs font-bold text-ink/60">
              <AlertTriangle className="h-3.5 w-3.5" /> Read-only
            </span>
          )}
        </div>
      </div>

      {!canEdit && (
        <div data-testid="emergency-readonly-notice" className="rounded-xl border border-sand-200 bg-sand-50 px-4 py-3 text-sm text-ink/60">
          You have read-only access to Emergency Escalation. Ask a manager to grant you configuration access to make changes.
        </div>
      )}

      {/* Tabs — incidents are the point of this page; the knobs live one tap away. */}
      <div data-testid="emergency-tabs" className="flex items-center gap-1 rounded-full bg-sand-100 p-1 w-fit max-w-full">
        <button type="button" data-testid="emergency-tab-incidents" onClick={() => setTab("incidents")}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${tab === "incidents" ? "bg-white text-ink shadow-sm" : "text-ink/55 hover:text-ink"}`}>
          <Clock className="h-4 w-4" /> Incidents
          {openIncidents > 0 ? <span className="rounded-full bg-red-600 text-white text-[11px] px-1.5 py-px">{openIncidents}</span>
            : events.length > 0 ? <span className="rounded-full bg-sand-200 text-ink/60 text-[11px] px-1.5 py-px">{events.length}</span> : null}
        </button>
        <button type="button" data-testid="emergency-tab-setup" onClick={() => setTab("setup")}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${tab === "setup" ? "bg-white text-ink shadow-sm" : "text-ink/55 hover:text-ink"}`}>
          <Users className="h-4 w-4" /> Ladder &amp; setup
          {showSetup && <span className="h-2 w-2 rounded-full bg-amber-500" title="Not finished" />}
          {unverifiedOnLadder.length > 0 && !showSetup && <span className="rounded-full bg-amber-100 text-amber-800 text-[11px] px-1.5 py-px">{unverifiedOnLadder.length} unverified</span>}
        </button>
      </div>

      {tab === "setup" && showSetup && (
        <section data-testid="emergency-setup-card" className="rounded-2xl border-2 border-danger-200 bg-danger-50/40 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-redline-600" />
              <h2 className="text-base font-extrabold text-ink">Finish setting up Emergency Alerts</h2>
            </div>
            <button data-testid="emergency-setup-finish" onClick={finishSetup}
              className="shrink-0 rounded-full bg-ink px-4 py-1.5 text-xs font-bold text-white hover:bg-ink-700">
              Mark as set up
            </button>
          </div>
          <p className="mt-1 text-xs text-ink/55">Three quick steps so real emergencies reach the right people. {V.many("person")} can report an emergency at any time — this is only about making sure your team gets alerted.</p>
          <div className="mt-4 space-y-3">
            {/* Step 1 */}
            <div className="flex items-start gap-3">
              {stepsDone[0] ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                : <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-ink/30 text-[11px] font-bold text-ink/50">1</span>}
              <div className="min-w-0">
                <div className="text-sm font-bold text-ink">Assign responders to your ladder</div>
                <div className="text-xs text-ink/55">{onLadder.length > 0 ? `${onLadder.length} responder${onLadder.length === 1 ? "" : "s"} on the ladder.` : "Pick who gets alerted in the On-call ladder below."}</div>
              </div>
            </div>
            {/* Step 2 */}
            <div className="flex items-start gap-3">
              {stepsDone[1] ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                : <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-ink/30 text-[11px] font-bold text-ink/50">2</span>}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-ink">Make sure they're reachable</div>
                {unverifiedOnLadder.length === 0 ? (
                  <div className="text-xs text-ink/55">{onLadder.length > 0 ? "Everyone on the ladder has a verified mobile." : "Add responders first."}</div>
                ) : (
                  <div className="mt-1">
                    <div className="text-xs font-semibold text-amber-700">{unverifiedOnLadder.length} responder{unverifiedOnLadder.length === 1 ? " has" : "s have"} no verified mobile — they'll be texted and emailed, but can't be called until verified:</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {unverifiedOnLadder.map((r) => (
                        <span key={r.user_id} className="inline-flex items-center gap-1 rounded-full bg-white border border-amber-200 px-2 py-0.5 text-[11px] font-medium text-ink">
                          <AlertTriangle className="h-3 w-3 text-amber-500" /> {r.name}
                        </span>
                      ))}
                    </div>
                    <button data-testid="emergency-remind-verify" onClick={() => remindVerify(unverifiedOnLadder.map((r) => r.user_id))} disabled={reminding}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-ink/20 bg-white px-3 py-1.5 text-xs font-semibold text-ink hover:border-ink disabled:opacity-50">
                      {reminding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Remind them to verify
                    </button>
                  </div>
                )}
              </div>
            </div>
            {/* Step 3 */}
            <div className="flex items-start gap-3">
              {stepsDone[2] ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                : <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-ink/30 text-[11px] font-bold text-ink/50">3</span>}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-ink">Send a test alert</div>
                <div className="text-xs text-ink/55">Confirm the chain works before you rely on it.</div>
                <button data-testid="emergency-setup-test" onClick={sendTest} disabled={testing || onLadder.length === 0}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-ink/20 bg-white px-3 py-1.5 text-xs font-semibold text-ink hover:border-ink disabled:opacity-50">
                  {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send test alert
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Active emergency banner */}
      {active && (() => {
        const iOwnIt = !!me && active.acked_by?.user_id === me;
        const canClose = canEdit || iOwnIt;
        return (
        <div data-testid="emergency-active-banner" className="rounded-2xl border-2 border-red-300 bg-red-50 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <ShieldAlert className="mt-0.5 h-6 w-6 shrink-0 text-red-600" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-extrabold uppercase tracking-wide text-red-700">Active emergency</span>
                  <StatusPill status={active.status} />
                </div>
                <p className="mt-1 text-sm text-ink">{active.issue}</p>
                <p className="mt-0.5 text-xs text-ink/60">
                  {fmtDate(active.created_at)}
                  {active.acked_by ? ` · Owned by ${iOwnIt ? "you" : active.acked_by.name} since ${fmtDate(active.acked_by.at)}` : ` · Alerted ${(active.alert_phones || []).length} responder(s), nobody has taken it yet`}
                  {active.ticket_ref ? ` · Request ${active.ticket_ref}` : ""}
                </p>
                {active.no_responders && (
                  <p className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-red-700"><PhoneOff className="h-3.5 w-3.5" /> No reachable responders — verify a phone number now.</p>
                )}
                <div className="mt-3"><ReporterBlock ev={active} /></div>
                <div className="mt-3 rounded-xl border border-red-200/70 bg-red-100/40 p-3" data-testid="emergency-active-contacted">
                  <ContactedPanel ev={active} />
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <button data-testid="emergency-resolve-btn" onClick={() => setResolveOpen(true)} disabled={!canClose}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                <CheckCircle2 className="h-4 w-4" /> Mark resolved
              </button>
              {!canClose && (
                <p data-testid="emergency-resolve-hint" className="max-w-[220px] text-right text-[11px] text-ink/50">
                  {active.acked_by ? `Only ${active.acked_by.name} (who took it) or a manager can close this.` : "Only a manager, or whoever takes it, can close this."}
                </p>
              )}
              {canClose && iOwnIt && !canEdit && (
                <p data-testid="emergency-resolve-owner-hint" className="max-w-[220px] text-right text-[11px] text-ink/50">You took this one — close it here or reply RESOLVED by text.</p>
              )}
              <button data-testid="emergency-active-record" onClick={() => openRecord(active.id)}
                className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-800 hover:bg-red-100">
                <History className="h-3.5 w-3.5" /> Timeline so far
              </button>
            </div>
          </div>
        </div>
        );
      })()}
      <ResolveDialog open={resolveOpen} onClose={() => setResolveOpen(false)} tenantId={tenantId} ev={active} onResolved={load} />
      <EmergencyTimelineDialog tenantId={tenantId} eventId={recordId} onClose={closeRecord} canEdit={canEdit} me={me} />
      <ModuleSettingsDialog module={cfgOpen ? "emergency" : null} tenant={tenant} tenantId={tenantId} onClose={() => setCfgOpen(false)} onSaved={() => { refreshTenants?.(); load(); }} />

      {tab === "incidents" && (
        <>
          {/* Event log — the record of what happened, front and center */}
          <section data-testid="emergency-log-card" className="rounded-2xl border border-sand-200 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-heading text-lg font-bold text-ink flex items-center gap-2"><Clock className="h-5 w-5 text-ink/60" /> Emergency log</h2>
              <span className="text-xs text-ink/50">{events.length ? `${events.length} on record` : "nothing yet"}</span>
            </div>
            {events.length === 0 ? (
              <p className="mt-4 text-sm text-ink/50">No emergencies recorded yet. When {V.a("person")} reports one, it shows up here with the full timeline.</p>
            ) : (
              <div className="mt-3 divide-y divide-sand-100" data-testid="emergency-log">
                {events.map((ev) => (
                  <div key={ev.id} data-testid={`emergency-event-${ev.id}`} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between">
                    <button type="button" data-testid={`emergency-event-open-${ev.id}`} onClick={() => openRecord(ev.id)} className="group min-w-0 flex-1 text-left">
                      <div className="flex items-start gap-2">
                        <StatusPill status={ev.status} />
                        <span className="min-w-0 text-sm font-semibold leading-snug text-ink group-hover:text-redline-600 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden" data-testid={`emergency-event-issue-${ev.id}`}>{ev.issue}</span>
                        {ev.drill && <span className="shrink-0 rounded-full bg-sand-100 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-ink/50">Drill</span>}
                      </div>
                      <div className="mt-1 text-xs leading-relaxed text-ink/55">
                        {ev.reporter_name || ev.resident_name || "Unknown reporter"}{reporterHome(ev) ? ` · ${reporterHome(ev)}` : ""}{ev.reporter_phone ? ` · ${fmtPhone(ev.reporter_phone)}` : ""} · {fmtDate(ev.created_at)}
                        {ev.acked_by ? ` · Owned by ${ev.acked_by.name}` : ""}
                        {ev.resolved_by ? ` · Resolved by ${ev.resolved_by}${ev.resolved_at_entered ? " (time entered)" : ""}${ev.all_clear_sent ? " · all-clear sent" : ""}` : ""}
                        {ev.ticket_ref ? ` · ${ev.ticket_ref}` : ""}
                      </div>
                    </button>
                    <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
                      <span className="text-xs text-ink/40">{(ev.alert_phones || []).length} alerted{(ev.reporters || []).length ? ` · ${ev.reporters.length + 1} reporters` : ""}</span>
                      <button type="button" data-testid={`emergency-event-timeline-${ev.id}`} onClick={() => openRecord(ev.id)} title="Timeline"
                        className="inline-flex items-center gap-1 rounded-full border border-sand-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-ink hover:border-ink">
                        <History className="h-3 w-3" /> Timeline
                      </button>
                      <IncidentPdfButton tenantId={tenantId} ev={ev} testid={`emergency-event-pdf-${ev.id}`} className="!px-2.5 !py-1 !text-[11px]" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <EmergencyReadiness tenantId={tenantId} canEdit={canEdit} autoOpenDrill={params.get("drill") === "1"}
            onDrillConsumed={() => { const p = new URLSearchParams(params); p.delete("drill"); setParams(p, { replace: true }); }}
            onChanged={load} onOpenRecord={openRecord} onRemind={remindVerify} refreshKey={`${saveTick}:${events.length}:${active?.id || ''}:${active?.status || ''}`} />
        </>
      )}

      {tab === "setup" && (
      <fieldset disabled={!canEdit} className="m-0 min-w-0 space-y-6 border-0 p-0 disabled:opacity-70">
      {/* On-call ladder */}
      <CollapsibleCard id="emergency.ladder" icon={Users} title="On-call ladder" testid="emergency-ladder-card"
        summary={<span data-testid="ladder-reach-summary">{onLadder.length} on the ladder · {callableCount} reachable by call</span>}>
        <p className="mt-3 text-xs text-ink/55">
          Each step lists <b>only the people on it</b> and has its own <b>Text · Call · Email</b> switches (press 1 on a call to take it).
          Only a <b>verified</b> mobile can be called — an unverified one is texted and emailed as a safety net. Verify numbers right here,
          or each person can from their profile menu &rarr; <b>Account settings</b>.
        </p>
        <LadderFlow tiers={tiers} responders={responders} cfg={cfg} canEdit={canEdit}
          onTiming={(p) => { if (p.tierWait) updateTier(p.tierWait[0], { wait_minutes: p.tierWait[1] }); else setCfg((c) => ({ ...c, ...p })); }} />

        {/* Always-visible reachability fix: verify (or add) a number for anyone on the ladder who
            can't be reached yet — the gap that left responders "not verified" with nowhere to fix it. */}
        {unverifiedOnLadder.length > 0 && (
          <div data-testid="ladder-unverified-banner" className="mt-3 rounded-xl border border-amber-200 bg-amber-50/70 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-amber-800">
                  {unverifiedOnLadder.length} responder{unverifiedOnLadder.length === 1 ? "" : "s"} on the ladder {unverifiedOnLadder.length === 1 ? "is" : "are"} unverified — texted and emailed, not called. {canEdit ? "Verify the mobile so the call reaches them too." : "Ask a manager to verify the mobile."}
                </div>
                <div className="mt-2 flex flex-col gap-1.5">
                  {unverifiedOnLadder.map((r) => (
                    <div key={r.user_id} data-testid={`ladder-unverified-${r.user_id}`} className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-ink">{r.name}</span>
                      <span className="text-[11px] text-ink/50">{r.phone ? `${r.phone} · not verified` : "no phone on file"}</span>
                      {canEdit && (
                        <button data-testid={`ladder-verify-${r.user_id}`} onClick={() => setVerifyTarget(r)}
                          className="ml-auto inline-flex items-center gap-1 rounded-full border border-ink/20 bg-white px-2.5 py-1 text-[11px] font-semibold text-ink hover:border-ink">
                          <Phone className="h-3 w-3" /> {r.phone ? "Verify now" : "Add & verify"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {canEdit && (
                  <button data-testid="ladder-remind-verify" onClick={() => remindVerify(unverifiedOnLadder.map((r) => r.user_id))} disabled={reminding}
                    className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-ink/60 hover:text-ink disabled:opacity-50">
                    {reminding ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Or email them a reminder to verify it themselves
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="mt-4 space-y-4">
          {tiers.map((tier, i) => {
            const isLast = i === tiers.length - 1;
            const ch = { sms: tier.channels?.sms !== false, call: tier.channels?.call !== false, email: !!tier.channels?.email };
            const setCh = (k) => {
              const next = { ...ch, [k]: !ch[k] };
              if (!next.sms && !next.call && !next.email) next.sms = true;   // a step can't be silent
              updateTier(i, { channels: next });
            };
            const ids = tier.user_ids || [];
            const assigned = ids.map((id) => responders.find((r) => r.user_id === id)).filter(Boolean);
            const available = responders.filter((r) => !ids.includes(r.user_id))
              .filter((r) => !respQuery.trim() || `${r.name} ${r.email}`.toLowerCase().includes(respQuery.trim().toLowerCase()));
            const picking = pickerTier === i;
            return (
            <div key={i} data-testid={`emergency-tier-${i}`} className="rounded-xl border border-sand-200 bg-sand-50/40 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink text-[11px] font-bold text-white">{i + 1}</span>
                <input data-testid={`emergency-tier-name-${i}`} value={tier.name || ""}
                  onChange={(e) => updateTier(i, { name: e.target.value })}
                  className="min-w-0 flex-1 rounded-lg border border-sand-300 bg-white px-3 py-1.5 text-sm font-semibold outline-none focus:ring-2 focus:ring-redline-500/30" />
                {!isLast && (
                  <span className="flex items-center gap-1.5 text-xs text-ink/60">
                    <Timer className="h-3.5 w-3.5" /> wait
                    <input type="number" min={1} data-testid={`emergency-tier-wait-${i}`} value={tier.wait_minutes ?? 7}
                      onChange={(e) => updateTier(i, { wait_minutes: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                      className="w-14 rounded-lg border border-sand-300 bg-white px-2 py-1 text-right text-sm outline-none focus:ring-2 focus:ring-redline-500/30" /> min
                  </span>
                )}
                {tiers.length > 1 && (
                  <button data-testid={`emergency-tier-remove-${i}`} onClick={() => removeTier(i)}
                    className="rounded-lg p-1.5 text-ink/40 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
              {/* How this step reaches people — set by the manager, per step. */}
              <p className="mt-2 text-[11px] text-ink/55" data-testid={`emergency-tier-together-${i}`}>
                <b>Everyone on this step is alerted together</b>{i === 0 ? " the moment an emergency fires" : ` — only if nobody on step ${i} has replied ACK within ${tiers[i - 1]?.wait_minutes ?? 7} min`}.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`emergency-tier-channels-${i}`}>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-ink/50 mr-1">Reach them by</span>
                {[["sms", "Text", MessageSquareText], ["call", "Call", PhoneCall], ["email", "Email", Mail]].map(([k, label, Icon]) => (
                  <button key={k} type="button" data-testid={`emergency-tier-${i}-channel-${k}`} onClick={() => setCh(k)} disabled={!canEdit}
                    aria-pressed={ch[k]}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${ch[k] ? "border-ink bg-ink text-white" : "border-sand-300 bg-white text-ink/60 hover:border-ink/40"}`}>
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
                <span className="text-[11px] text-ink/50 ml-1">
                  {ch.call && !ch.sms ? "Anyone who can't be called is texted instead." : ""}
                  {!ch.email ? " Unverified mobiles also get an email as a safety net." : ""}
                </span>
              </div>
              <div className="mt-3 space-y-1.5">
                {assigned.length === 0 && (
                  <p data-testid={`emergency-tier-${i}-empty`} className="rounded-lg border border-dashed border-sand-300 bg-white px-3 py-2.5 text-sm text-ink/50">
                    Nobody on this step yet — add a responder below.
                  </p>
                )}
                {assigned.map((r) => (
                  <div key={r.user_id} data-testid={`emergency-tier-${i}-responder-${r.user_id}`}
                    className="flex w-full min-w-0 items-center gap-2.5 rounded-lg border border-sand-200 bg-white px-3 py-2">
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-medium text-ink">{r.name}</span>
                        <span className="shrink-0 rounded-full bg-sand-100 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-ink/50">{r.role === "board_member" ? `${V.Grp()}` : "Manager"}</span>
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                        {r.phone ? <span className="truncate text-ink/60">{r.phone}</span> : <span className="text-amber-700">no mobile on file</span>}
                        {(r.numbers || []).length > 1 && <span data-testid={`emergency-responder-${r.user_id}-numbers`} title={(r.numbers || []).map((n) => `${n.number}${n.verified ? " ✓" : ""}`).join(", ")} className="rounded-full bg-sand-100 px-1.5 py-px text-[10px] text-ink/60">+{r.numbers.length - 1} more number{r.numbers.length > 2 ? "s" : ""}</span>}
                        <ReachabilityBadge phoneVerified={r.phone_verified} hasPhone={!!r.phone} callOk={r.call_ok !== false} />
                        {ch.call && r.call_ok === false && <span className="rounded-full bg-sand-100 px-1.5 py-px text-[10px] text-ink/60">turned calls off for themselves</span>}
                        {ch.email && !r.email && <span className="rounded-full bg-amber-50 px-1.5 py-px text-[10px] text-amber-700">no email</span>}
                      </span>
                    </span>
                    {canEdit && (
                      <button type="button" data-testid={`emergency-tier-${i}-remove-${r.user_id}`} onClick={() => toggleInTier(i, r.user_id)}
                        title="Remove from this step" className="rounded-lg p-1.5 text-ink/40 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                ))}
                {canEdit && !picking && (
                  <button type="button" data-testid={`emergency-tier-${i}-add`} onClick={() => { setPickerTier(i); setRespQuery(""); }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-sand-300 bg-white px-3 py-1.5 text-xs font-semibold text-ink/70 hover:border-ink hover:text-ink">
                    <Plus className="h-3.5 w-3.5" /> Add responder
                  </button>
                )}
                {picking && (
                  <div data-testid={`emergency-tier-${i}-picker`} className="rounded-xl border border-sand-200 bg-white p-2">
                    <div className="flex items-center gap-2 px-1 pb-2">
                      <input autoFocus data-testid="emergency-responder-search" value={respQuery} onChange={(e) => setRespQuery(e.target.value)}
                        placeholder={`Search managers and ${V.lcs("board")}…`}
                        className="min-w-0 flex-1 rounded-lg border border-sand-200 px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                      <button type="button" data-testid={`emergency-tier-${i}-picker-done`} onClick={() => setPickerTier(null)} className="text-xs font-semibold text-ink/60 hover:text-ink">Done</button>
                    </div>
                    {available.length === 0 && <p className="px-2 pb-1 text-xs text-ink/50">{responders.length ? "Everyone is already on this step." : `No managers or ${V.lcs("board")} yet — add them under Access & Team.`}</p>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto">
                      {available.map((r) => (
                        <button key={r.user_id} type="button" data-testid={`emergency-tier-${i}-pick-${r.user_id}`} onClick={() => toggleInTier(i, r.user_id)}
                          className="flex w-full min-w-0 items-center gap-2.5 rounded-lg border border-sand-200 px-3 py-2 text-left hover:border-ink/30 hover:bg-sand-50">
                          <Plus className="h-3.5 w-3.5 shrink-0 text-ink/40" />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5">
                              <span className="truncate text-sm font-medium text-ink">{r.name}</span>
                              <span className="shrink-0 rounded-full bg-sand-100 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-ink/50">{r.role === "board_member" ? `${V.Grp()}` : "Manager"}</span>
                            </span>
                            <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                              {r.phone ? <span className="truncate text-ink/60">{r.phone}</span> : <span className="text-amber-700">no mobile on file</span>}
                              <ReachabilityBadge phoneVerified={r.phone_verified} hasPhone={!!r.phone} callOk={r.call_ok !== false} />
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );})}
        </div>
        <button data-testid="emergency-add-tier" onClick={addTier}
          className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-dashed border-sand-300 px-4 py-2 text-sm font-semibold text-ink/70 hover:border-ink hover:text-ink">
          <Plus className="h-4 w-4" /> Add a tier
        </button>
      </CollapsibleCard>

      {/* Timing, follow-up, calls, FYI, drills — set-once switches live in Module settings (same sheet as Settings → Features ⚙). */}
      <div className="rounded-2xl border border-dashed border-sand-300 bg-sand-50/60 px-5 py-3 text-sm text-ink/70 flex flex-wrap items-center justify-between gap-2" data-testid="emergency-settings-pointer">
        <span>Re-alert method, calls, {V.grp()} FYI, photo ask and drills are set once under <span className="font-semibold text-ink">Module settings</span>. Step waits and timeouts are edited right on the ladder above.</span>
        {canEdit && <button type="button" onClick={() => setCfgOpen(true)} className="font-semibold text-redline-600 hover:text-redline-700">Open Module settings →</button>}
      </div>

      {/* Detection settings — YOUR words. Owner: "who decided my keywords and why can't I customize
          them?" They always could; the card was collapsed and unlabelled. New id so a saved
          "collapsed" state from before doesn't hide it again. */}
      <CollapsibleCard id="emergency.words" icon={ListChecks} title="Your emergency words — add or remove any" testid="emergency-detection-card" defaultOpen={true} openSignal={wantDetection}
        summary={`${(cfg.keywords || []).length} words · ${(cfg.critical_keywords || []).length} critical · ${cfg.require_confirmation === false ? "no confirm" : "asks to confirm"}${cfg.ai_backstop === false ? "" : " · AI backstop"}`}>
        <p className="mt-3 text-xs text-ink/55" data-testid="emergency-words-intro">
          These are just starting words. Add anything {V.lcs("person")} here actually say, delete what doesn't apply — one word or phrase per line.
          A message containing any of them is treated as a possible emergency; the AI backstop catches phrasings that don't match.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <WordsEditor label="Emergency words" testid="emergency-keywords" canEdit={canEdit} defaults={defaults?.keywords}
            help={`${V.A("person")} message matching any of these is treated as a possible emergency.`}
            words={cfg.keywords || []} onChange={(w) => setCfg((c) => ({ ...c, keywords: w }))} />
          <WordsEditor label="Critical words" testid="emergency-critical-keywords" canEdit={canEdit} defaults={defaults?.critical_keywords} tone="red"
            help="Life-safety hazards. Can skip confirmation if enabled below."
            words={cfg.critical_keywords || []} onChange={(w) => setCfg((c) => ({ ...c, critical_keywords: w }))} />
        </div>
        <PhraseTester tenantId={tenantId} keywords={cfg.keywords || []} criticalKeywords={cfg.critical_keywords || []} aiBackstop={cfg.ai_backstop !== false} />
        <div className="mt-4 space-y-2">
          <Toggle testid="emergency-toggle-confirm" checked={!!cfg.require_confirmation}
            onChange={(v) => setCfg((c) => ({ ...c, require_confirmation: v }))}
            label={`Confirm with the ${V.lc("person")} before alerting`}
            desc={`Asks the ${V.lc("person")} to reply YES first — prevents false alarms.`} />
          <Toggle testid="emergency-toggle-critical-skip" checked={!!cfg.critical_skip_confirm}
            onChange={(v) => setCfg((c) => ({ ...c, critical_skip_confirm: v }))}
            label="Critical keywords skip confirmation"
            desc="Fire/gas/break-in alert the team immediately without waiting for a reply." />
          <Toggle testid="emergency-toggle-ai" checked={!!cfg.ai_backstop}
            onChange={(v) => setCfg((c) => ({ ...c, ai_backstop: v }))}
            label="AI backstop"
            desc="If no keyword matches, let the AI catch emergencies phrased differently." />
        </div>
      </CollapsibleCard>

      {/* Message templates */}
      <CollapsibleCard id="emergency.wording" icon={MessageSquareText} title="Message wording" testid="emergency-wording-card" defaultOpen={false}
        summary={`${MSG_FIELDS.length} fixed templates · never AI-written`}>
        <p className="mt-3 text-xs text-ink/50">Use placeholders like {`{community}`}, {"{issue}"}, {"{reporter}"}, {"{unit}"}, {"{reporter_phone}"}, {"{roster_flag}"}, {"{responder}"}, {"{responder_title}"}, {"{link}"}.</p>
        {["resident", "responder"].map((g) => (
          <div key={g} className="mt-4" data-testid={`emergency-msg-group-${g}`}>
            <div className={`rounded-xl px-3 py-2 text-xs font-bold ${g === "resident" ? "bg-red-50 text-red-800" : "bg-sand-50 text-ink/70"}`}>
              {g === "resident" ? `To the ${V.lc("person")} — the ONLY texts a reporter can ever receive from this module. Fixed wording, never written by the AI.` : "To your responders"}
            </div>
            <div className="mt-3 space-y-4">
              {MSG_FIELDS.filter((f) => f.group === g).map((f) => (
                <div key={f.key}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <label className="text-xs font-bold text-ink">{f.label}</label>
                      <span className="ml-2 text-[11px] text-ink/40">{f.hint}</span>
                    </div>
                    <TextAssist value={cfg.messages?.[f.key] || ""} onChange={(next) => setMsg(f.key, next)}
                      surface="resident_message" testId={`emergency-msg-assist-${f.key}`} micTitle="Dictate the wording" />
                  </div>
                  <textarea data-testid={`emergency-msg-${f.key}`} value={cfg.messages?.[f.key] || ""}
                    onChange={(e) => setMsg(f.key, e.target.value)} rows={2}
                    className="mt-1 w-full resize-y rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </CollapsibleCard>
      </fieldset>
      )}

      {/* Admin-assisted verify dialog — reachable from the ladder banner */}
      <Dialog open={!!verifyTarget} onOpenChange={(o) => !o && setVerifyTarget(null)}>
        <DialogContent className="max-w-md" data-testid="ladder-verify-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Phone className="h-4 w-4 text-redline-500" /> Verify {verifyTarget?.name || "responder"}&rsquo;s mobile</DialogTitle>
            <DialogDescription className="sr-only">Send a verification code to this responder&apos;s mobile and confirm it.</DialogDescription>
          </DialogHeader>
          {verifyTarget && (
            <div className="space-y-3">
              <p className="text-xs text-ink/60">
                We&rsquo;ll text a 6-digit code to this number and mark it verified once it&rsquo;s entered — then this responder can be texted and called for emergencies. If this is your own number, the code comes to your phone.
              </p>
              <AdminPhoneVerify userId={verifyTarget.user_id} initialPhone={verifyTarget.phone || ""} showInput
                verified={false} onVerified={() => { setVerifyTarget(null); load(); }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
      {canEdit && tab === "setup" && <StickySaveBar dirty={dirty} saving={saving} onSave={save} onDiscard={discard} label="Emergency settings changed" testid="emergency-sticky" />}
      {dirty && <div className="h-20" aria-hidden="true" />}
    </div>
  );
}
